import React, { useEffect, useState } from "react";
import hrApi from "./hrApi";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UserCheck, ArrowLeftRight, Undo2, ChevronDown, ChevronRight } from "lucide-react";

function NameRow({ r, onOpen, done }) {
  const [first, setFirst] = useState(r.first_name || "");
  const [last, setLast] = useState(r.family_name || "");
  const changed = first !== (r.first_name || "") || last !== (r.family_name || "");
  const call = async (fn, msg) => { try { await fn(); toast.success(msg); done(); } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); } };
  const save = () => call(async () => { await hrApi.put(`/employees/${r.id}`, { first_name: first, family_name: last }); await hrApi.post(`/employees/${r.id}/name-ok`); }, "Name saved");
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-amber-100" data-testid={`name-check-${r.id}`}>
      <button onClick={() => onOpen(r.id)} className="text-sm font-semibold text-slate-800 hover:underline min-w-[10rem] text-left">{r.name}</button>
      <div className="grid grid-cols-2 gap-2 w-full sm:w-80">
        <Input data-testid={`name-first-${r.id}`} value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First name" className="h-8 text-xs" />
        <Input data-testid={`name-last-${r.id}`} value={last} onChange={(e) => setLast(e.target.value)} placeholder="Last name" className="h-8 text-xs uppercase" />
      </div>
      <span className="text-[11px] text-slate-500 flex-1 min-w-[12rem]">{r.name_note}</span>
      {changed
        ? <Button size="sm" data-testid={`name-save-${r.id}`} onClick={save} className="h-8 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white">Save</Button>
        : <Button size="sm" data-testid={`name-ok-${r.id}`} onClick={() => call(() => hrApi.post(`/employees/${r.id}/name-ok`), "Marked as right")} className="h-8 rounded-full bg-teal-500 hover:bg-teal-400 text-white gap-1"><UserCheck size={13} /> Looks right</Button>}
      <Button size="sm" variant="outline" data-testid={`name-swap-${r.id}`} onClick={() => call(() => hrApi.post(`/employees/${r.id}/name-swap`), "First and last swapped")} className="h-8 rounded-full gap-1"><ArrowLeftRight size={13} /> Swap</Button>
      {r.name_before && <Button size="sm" variant="outline" data-testid={`name-undo-${r.id}`} onClick={() => call(() => hrApi.post(`/employees/${r.id}/name-undo`), "Old name restored")} className="h-8 rounded-full gap-1"><Undo2 size={13} /> Keep “{r.name_before}”</Button>}
    </div>
  );
}

export function NamesCheck({ onOpen, reload }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => hrApi.get("/names-check").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!rows.length) return null;
  const done = () => { load(); reload && reload(); };
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl overflow-hidden" data-testid="names-check">
      <button onClick={() => setOpen(!open)} data-testid="names-check-toggle" className="w-full flex flex-wrap items-center gap-x-2 gap-y-0.5 px-4 py-3 text-left">
        {open ? <ChevronDown size={16} className="text-amber-700" /> : <ChevronRight size={16} className="text-amber-700" />}
        <span className="font-heading font-bold text-amber-900 whitespace-nowrap" data-testid="names-check-count">{rows.length} name{rows.length === 1 ? "" : "s"} to check</span>
        <span className="text-xs text-amber-800 basis-full sm:basis-auto pl-6 sm:pl-0">changed from your MANPOWER file or not found in it. Fix, swap or confirm each one.</span>
      </button>
      {open && <div className="bg-white/70 max-h-[60vh] overflow-y-auto">{rows.map((r) => <NameRow key={r.id} r={r} onOpen={onOpen} done={done} />)}</div>}
    </div>
  );
}
